import { parseXmlDocument } from "./parseTinglysningsDataXml";
import type { ServitutDokumentAnmelder } from "./parseServitutDokumentXml";

type XmlEl = ReturnType<typeof parseXmlDocument>;

export type PaategningFilingKind = "servitut" | "paategning" | "matrikulaerAendring" | "andet";

export type ServitutFilingTextGroup = {
   heading: string;
   paragraphs: string[];
};

export type ServitutPaategningFiling = {
   id: string;
   tidspunkt: string;
   ekspeditionstype: string;
   kind: PaategningFilingKind;
   anmelder: ServitutDokumentAnmelder | null;
   bilagRefs: string[];
   textGroups: ServitutFilingTextGroup[];
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function directChildren(el: XmlEl | undefined, tag: string): XmlEl[] {
   return el?.children.filter((child) => child.tag === tag) ?? [];
}

function firstDirect(el: XmlEl | undefined, tag: string): XmlEl | undefined {
   return directChildren(el, tag)[0];
}

function firstDescendant(el: XmlEl | undefined, tag: string): XmlEl | undefined {
   if (!el) {
      return undefined;
   }
   if (el.tag === tag) {
      return el;
   }
   for (const child of el.children) {
      const found = firstDescendant(child, tag);
      if (found) {
         return found;
      }
   }
   return undefined;
}

function collectByTag(el: XmlEl | undefined, tag: string): XmlEl[] {
   if (!el) {
      return [];
   }
   const found: XmlEl[] = [];
   const walk = (node: XmlEl) => {
      if (node.tag === tag) {
         found.push(node);
      }
      node.children.forEach(walk);
   };
   walk(el);
   return found;
}

function textOf(el: XmlEl | undefined, tag?: string): string {
   const target = tag ? firstDescendant(el, tag) : el;
   return (target?.text ?? "").trim();
}

function xmlInputToText(input: Document | string | null | undefined): string {
   if (!input) {
      return "";
   }
   if (typeof input === "string") {
      return input;
   }
   if (typeof XMLSerializer !== "undefined") {
      return new XMLSerializer().serializeToString(input);
   }
   return input.documentElement?.outerHTML || "";
}

function filingKindFromAnmeldelse(tingbog: XmlEl | undefined, ekspeditionstype: string): PaategningFilingKind {
   const filingTag = tingbog?.children[0]?.tag || "";
   if (filingTag === "ServitutKombinationPaategn" || ekspeditionstype === "106") {
      return "paategning";
   }
   if (filingTag === "ServitutOpret" || ekspeditionstype === "50") {
      return "servitut";
   }
   if (filingTag === "EjendomOpret" || ekspeditionstype === "185") {
      return "matrikulaerAendring";
   }
   return "andet";
}

function parseSnapshotNames(root: XmlEl): Map<string, string> {
   const names = new Map<string, string>();
   const snapshot = firstDescendant(root, "SnapshotSamling");
   if (!snapshot) {
      return names;
   }
   const walk = (node: XmlEl) => {
      if (node.tag === "RolleStruktur") {
         const name = textOf(firstDirect(node, "Navn")) || textOf(node, "LegalUnitName");
         const cvr = textOf(node, "CVRnumberIdentifier");
         if (name && cvr) {
            names.set(`cvr:${cvr}`, name);
         }
         return;
      }
      node.children.forEach(walk);
   };
   walk(snapshot);
   return names;
}

function parseAnmelder(svar: XmlEl): ServitutDokumentAnmelder | null {
   const info = firstDescendant(svar, "AnmelderInformation");
   if (!info) {
      return null;
   }
   const names = parseSnapshotNames(svar);
   const cvr = textOf(info, "CVRnumberIdentifier");
   const anmelder: ServitutDokumentAnmelder = {
      cvr,
      name: (cvr && names.get(`cvr:${cvr}`)) || textOf(info, "LegalUnitName") || textOf(info, "PersonName"),
      phone: textOf(info, "TelephoneNumberIdentifier"),
      email: textOf(info, "EmailAddressIdentifier"),
   };
   if (!anmelder.cvr && !anmelder.name) {
      return null;
   }
   return anmelder;
}

function parseBilagRefs(svar: XmlEl): string[] {
   const refs: string[] = [];
   const seen = new Set<string>();
   for (const el of collectByTag(firstDescendant(svar, "AnmeldelseInformation") ?? svar, "BilagReference")) {
      const value = el.text.trim();
      if (!UUID_RE.test(value) || seen.has(value.toLowerCase())) {
         continue;
      }
      seen.add(value.toLowerCase());
      refs.push(value);
   }
   return refs;
}

function splitStatuteParagraphs(text: string): string[] {
   const parts = text.split(/(?<=(?:^|[.!?]\s+))(?=§\s+\d+\b)/).map((part) => part.trim()).filter(Boolean);
   return parts.length > 1 ? parts : [text];
}

function textGroupFromTekstEl(el: XmlEl | undefined): ServitutFilingTextGroup {
   const heading = textOf(firstDirect(el, "Overskrift"));
   const paragraphs = directChildren(el, "Afsnit")
      .flatMap((afsnit) => splitStatuteParagraphs(afsnit.text.trim()))
      .filter(Boolean);
   return { heading, paragraphs };
}

function parseFraseMap(root: XmlEl | undefined): Map<string, XmlEl> {
   const frases = new Map<string, XmlEl>();
   for (const frase of collectByTag(firstDescendant(root, "FraseSamling"), "Frase")) {
      const id = textOf(firstDirect(frase, "FraseIdentifikator"));
      if (id) {
         frases.set(id, frase);
      }
   }
   return frases;
}

function parseTekstSamling(
   samling: XmlEl | undefined,
   fraseMap: Map<string, XmlEl>,
): ServitutFilingTextGroup[] {
   if (!samling) {
      return [];
   }
   const groups: ServitutFilingTextGroup[] = [];
   for (const tekst of directChildren(samling, "ServitutTekst")) {
      const fraseId = textOf(firstDirect(tekst, "FraseIdentifikator"));
      const frase = fraseId ? fraseMap.get(fraseId) : undefined;
      if (frase) {
         groups.push(textGroupFromTekstEl(firstDirect(frase, "FraseTekst") ?? frase));
         continue;
      }
      const group = firstDirect(tekst, "TekstGruppe");
      if (group) {
         groups.push(textGroupFromTekstEl(group));
      }
   }
   return cleanFilingTextGroups(groups);
}

function cleanFilingTextGroups(groups: ServitutFilingTextGroup[]): ServitutFilingTextGroup[] {
   const cleaned = groups
      .map((group) => ({
         heading: group.heading.trim(),
         paragraphs: group.paragraphs.map((paragraph) => paragraph.trim()).filter(Boolean),
      }))
      .filter((group) => group.heading || group.paragraphs.length);

   const result: ServitutFilingTextGroup[] = [];
   for (let index = 0; index < cleaned.length; index += 1) {
      const group = cleaned[index];
      const titleOnly = !group.heading && group.paragraphs.length === 1;
      const title = (group.heading || group.paragraphs[0] || "").trim();
      if (titleOnly) {
         const laterHasTitle = cleaned.slice(index + 1).some((other) => (
            other.heading === title
            || other.heading.startsWith(title)
            || other.paragraphs[0] === title
         ));
         if (laterHasTitle) {
            continue;
         }
         result.push({ heading: title, paragraphs: [] });
         continue;
      }
      result.push({
         heading: group.heading,
         paragraphs: group.paragraphs.filter((paragraph) => paragraph !== group.heading),
      });
   }
   return result.filter((group) => group.heading || group.paragraphs.length);
}

export function filingTextIsSubstantial(groups: ServitutFilingTextGroup[]): boolean {
   return groups.some((group) => group.paragraphs.some((paragraph) => paragraph.length > 40 || /§/.test(paragraph)));
}

export function formatFilingTextGroups(groups: ServitutFilingTextGroup[]): string {
   return groups
      .map((group) => [group.heading, ...group.paragraphs].filter(Boolean).join("\n"))
      .filter(Boolean)
      .join("\n\n")
      .trim();
}

export function resolveCurrentEasementText(
   xmlDoc: Document | string | null | undefined,
   filings: ServitutPaategningFiling[],
): ServitutFilingTextGroup[] {
   const currentGroups = parseServitutTekstGroups(xmlDoc);
   if (filingTextIsSubstantial(currentGroups)) {
      return currentGroups;
   }
   const originalGroups = filings.find((filing) => filing.kind === "servitut")?.textGroups ?? [];
   return filingTextIsSubstantial(originalGroups) ? originalGroups : currentGroups;
}

export function parseServitutTekstGroups(input: Document | string | null | undefined): ServitutFilingTextGroup[] {
   const xml = xmlInputToText(input).trim();
   if (!xml) {
      return [];
   }
   try {
      const root = parseXmlDocument(xml);
      const servitut = firstDescendant(root, "Servitut");
      const samling = firstDescendant(servitut, "ServitutTekstSamling")
         || firstDescendant(root, "ServitutTekstSamling");
      return parseTekstSamling(samling, parseFraseMap(root));
   } catch {
      return [];
   }
}

function parseFilingTextGroups(svar: XmlEl): ServitutFilingTextGroup[] {
   const tingbog = firstDirect(svar, "AnmeldelseTingbog");
   return parseTekstSamling(firstDescendant(tingbog, "ServitutTekstSamling"), parseFraseMap(svar));
}

function parseFiling(svar: XmlEl): ServitutPaategningFiling | null {
   const id = textOf(firstDirect(svar, "AnmeldelseIdentifikator"));
   const tidspunkt = textOf(firstDirect(svar, "AnmeldelsesTidspunkt"));
   if (!id && !tidspunkt) {
      return null;
   }
   const tingbog = firstDirect(svar, "AnmeldelseTingbog");
   const stamp = firstDescendant(svar, "TinglysningspaategningElement");
   const ekspeditionstype = textOf(firstDirect(stamp, "EkspeditionstypeIdentifikator"))
      || textOf(tingbog, "EkspeditionstypeIdentifikator");
   return {
      id,
      tidspunkt,
      ekspeditionstype,
      kind: filingKindFromAnmeldelse(tingbog, ekspeditionstype),
      anmelder: parseAnmelder(svar),
      bilagRefs: parseBilagRefs(svar),
      textGroups: parseFilingTextGroups(svar),
   };
}

export function parseDokumentRevisionssporXml(input: Document | string | null | undefined): ServitutPaategningFiling[] {
   const xml = xmlInputToText(input).trim();
   if (!xml) {
      return [];
   }
   try {
      const root = parseXmlDocument(xml);
      return collectByTag(root, "AnmeldelseSvar")
         .filter((svar) => firstDirect(svar, "AnmeldelseTingbog"))
         .map(parseFiling)
         .filter((filing): filing is ServitutPaategningFiling => Boolean(filing))
         .reverse();
   } catch {
      return [];
   }
}

export function formatPaategningFilingType(kind: PaategningFilingKind): string {
   if (kind === "paategning") {
      return "Endorsement";
   }
   if (kind === "servitut") {
      return "Servitut";
   }
   if (kind === "matrikulaerAendring") {
      return "Cadastral change";
   }
   return "";
}
